import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { useTranslate } from "helpers";

import {
    getTireTypesAPI,
    getTireWidthsAPI,
    getTireHeightsAPI,
    getTireRadiusAPI,
    updateFleetTireAPI,
} from "services/api/fleet/tires/api";
import { getTireManufacturersAPI } from "services/api/calculations/tire/api";
import {
    FleetTireManufacturer,
    TireType,
    TireWidth,
    TireHeight,
    TireRadius,
} from "services/api/fleet/tires/types";
import { FleetVehicleTireRow } from "services/api/fleet/vehicles/tires/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { PaginationDirection } from "services/url";

import { Loader } from "ui/components/Loader";

import {
    TireRowEditMap,
    TireRowEditState,
    initTireRowEditState,
} from "../types";
import TireAxleGroup from "./TireAxleGroup";
import { TireInlineTableProps } from "./types";

export interface TireInlineTableRef {
    /** Sprema sve promijenjene redove. Vraća broj spremljenih, -1 na grešku. */
    saveAll: () => Promise<number>;
}

/** Labele pozicija guma za 2-osovinska vozila */
const FRONT_LABELS = ["Prednja lijeva", "Prednja desna"];
const REAR_LABELS = ["Stražnja lijeva", "Stražnja desna"];

/** Vraća label za osovinu ovisno o ukupnom broju osovina */
const getAxleLabel = (position: number, totalAxles: number): string => {
    if (totalAxles <= 2) {
        return position === 1 ? "Prednja osovina" : "Stražnja osovina";
    }
    return `Osovina ${position}`;
};

/** Vraća labele za gume unutar osovine */
const getTireLabels = (position: number, count: number, totalAxles: number): string[] => {
    if (totalAxles <= 2) {
        const labels = position === 1 ? FRONT_LABELS : REAR_LABELS;
        return Array.from({ length: count }, (_, i) => labels[i] ?? `${i + 1}`);
    }
    return Array.from({ length: count }, (_, i) => `${i + 1}`);
};

/** Provjera jesu li dva edit stanja jednaka po dimenzijama */
const areDimensionsSame = (a: TireRowEditState, b: TireRowEditState): boolean =>
    a.width === b.width && a.height === b.height && a.radius === b.radius;

/** Tablica guma grupirana po osovinama s inline uređivanjem */
const TireInlineTable = forwardRef<TireInlineTableRef, TireInlineTableProps>((props, ref) => {
    const { tires, onDeleteClick, onEditChange } = props;
    const { t } = useTranslate();

    // Dropdown opcije
    const [tireTypes, setTireTypes] = useState<TireType[]>([]);
    const [tireWidths, setTireWidths] = useState<TireWidth[]>([]);
    const [tireHeights, setTireHeights] = useState<TireHeight[]>([]);
    const [tireRadiusList, setTireRadiusList] = useState<TireRadius[]>([]);
    const [tireManufacturers, setTireManufacturers] = useState<FleetTireManufacturer[]>([]);

    // Stanje za inline uređivanje
    const [editMap, setEditMap] = useState<TireRowEditMap>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Dohvati dropdown opcije jednom na mount
    useEffect(() => {
        fetchDropdownOptions();
    }, []);

    // Inicijaliziraj edit mapu kad se promijene gume iz baze
    useEffect(() => {
        const newMap: TireRowEditMap = {};
        tires.forEach(tire => {
            newMap[tire.id] = editMap[tire.id]?.isDirty
                ? editMap[tire.id]
                : initTireRowEditState(tire);
        });
        setEditMap(newMap);
    }, [tires]);

    /** Dohvaća sve dropdown opcije paralelno */
    const fetchDropdownOptions = async () => {
        const defaultPagination = {
            pageNumber: 1,
            pageSize: 100,
            sort: { sortBy: "name", direction: PaginationDirection.Ascending },
        };
        try {
            const [typesRes, widthsRes, heightsRes, radiusRes, manufacturersRes] = await Promise.all([
                getTireTypesAPI(defaultPagination),
                getTireWidthsAPI(),
                getTireHeightsAPI(),
                getTireRadiusAPI(),
                getTireManufacturersAPI(defaultPagination),
            ]);
            setTireTypes(typesRes.data);
            setTireWidths(widthsRes);
            setTireHeights(heightsRes);
            setTireRadiusList(radiusRes);
            setTireManufacturers(manufacturersRes.data);
        } catch {
            // dropdown opcije ostaju prazne
        } finally {
            setIsLoading(false);
        }
    };

    // DropdownOption nizovi
    const tireTypeOptions: DropdownOption[] = useMemo(() =>
        tireTypes.map(i => ({ value: i.id, text: i.name })), [tireTypes]);
    const widthOptions: DropdownOption[] = useMemo(() =>
        tireWidths.map(i => ({ value: i.id, text: i.name })), [tireWidths]);
    const heightOptions: DropdownOption[] = useMemo(() =>
        tireHeights.map(i => ({ value: i.id, text: i.name })), [tireHeights]);
    const radiusOptions: DropdownOption[] = useMemo(() =>
        tireRadiusList.map(i => ({ value: i.id, text: i.name })), [tireRadiusList]);
    const manufacturerOptions: DropdownOption[] = useMemo(() =>
        tireManufacturers.map(i => ({ value: i.id, text: i.name })), [tireManufacturers]);

    // Grupiraj gume po axlePosition
    const axleGroups = useMemo(() => {
        const groups = new Map<number, FleetVehicleTireRow[]>();
        tires.forEach(tire => {
            const pos = tire.axlePosition ?? 1;
            if (!groups.has(pos)) groups.set(pos, []);
            groups.get(pos)!.push(tire);
        });
        return Array.from(groups.entries()).sort(([a], [b]) => a - b);
    }, [tires]);

    // Provjeri jesu li sve osovine identične po dimenzijama
    const allSameDimensions = useMemo(() => {
        if (axleGroups.length <= 1) return true;
        const firstGroupTires = axleGroups[0][1];
        const firstState = editMap[firstGroupTires[0]?.id];
        if (!firstState) return true;
        return axleGroups.every(([, groupTires]) => {
            const state = editMap[groupTires[0]?.id];
            return state ? areDimensionsSame(firstState, state) : true;
        });
    }, [editMap, axleGroups]);

    /** Promjena polja na osovini — ažurira sve gume u grupi */
    const handleAxleChange = useCallback((tireIds: string[], field: string, value: any) => {
        setEditMap(prev => {
            const updated = { ...prev };
            for (const id of tireIds) {
                if (updated[id]) {
                    const newDirtyFields = new Set(updated[id].dirtyFields);
                    newDirtyFields.add(field);
                    updated[id] = { ...updated[id], [field]: value, isDirty: true, dirtyFields: newDirtyFields };
                }
            }

            // Resolve nazive i proslijedi summary
            const firstId = Object.keys(updated)[0];
            if (firstId && onEditChange) {
                const first = updated[firstId];
                onEditChange({
                    tireDimensions: first.width && first.height && first.radius
                        ? `${first.width}/${first.height}/R${first.radius}` : null,
                    tireManufacturer: tireManufacturers.find(m => m.id === first.tireManufacturerId)?.name ?? null,
                    tireType: tireTypes.find(t => t.id === first.tireTypeId)?.name ?? null,
                    dot: first.dot,
                    profileDepth: first.profileDepth,
                    rof: first.rof,
                });
            }

            return updated;
        });
    }, [onEditChange, tireManufacturers, tireTypes]);

    /** Sprema sve promijenjene gume — poziva se iz wizarda na "Nastavi" */
    useImperativeHandle(ref, () => ({
        saveAll: async (): Promise<number> => {
            const dirtyIds = Object.keys(editMap).filter(id => editMap[id].isDirty);
            if (dirtyIds.length === 0) return 0;

            setIsSaving(true);
            try {
                await Promise.all(dirtyIds.map(tireId => {
                    const row = editMap[tireId];
                    return updateFleetTireAPI(tireId, {
                        id: tireId,
                        width: row.width,
                        height: row.height,
                        radius: row.radius,
                        quantity: 1,
                        dot: row.dot,
                        profileDepth: row.profileDepth,
                        rof: row.rof,
                        tireManufacturerId: row.tireManufacturerId,
                        tireTypeId: row.tireTypeId,
                        axlePosition: row.axlePosition,
                    }, t);
                }));

                setEditMap(prev => {
                    const updated = { ...prev };
                    dirtyIds.forEach(id => {
                        updated[id] = { ...updated[id], isDirty: false };
                    });
                    return updated;
                });

                setIsSaving(false);
                return dirtyIds.length;
            } catch {
                setIsSaving(false);
                return -1;
            }
        }
    }), [editMap, t]);

    const allIds = useMemo(() => tires.map(t => t.id), [tires]);

    if (isLoading) {
        return (
            <div className="relative flex items-center justify-center h-[200px]">
                <Loader isLoading={true}> </Loader>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white rounded-sm border">
                <table className="min-w-full whitespace-nowrap border-collapse p-0">
                    <thead className="sticky top-0 z-10 bg-white [&>tr>th]:border-b [&>tr>th]:border-b-[#ebebeb]">
                        <tr>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]" colSpan={2}>Osovina / Guma</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.width")}</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.height")}</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.radius")}</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.manufacturer")}</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.tiretype")}</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.dot")}</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.profiledepth")}</th>
                            <th className="px-4 pt-3 pb-2 text-xs font-normal text-center text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.rof")}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white w-full">
                        {(() => {
                            /** Izračunaj union dirty fields za grupu tire ID-jeva */
                            const getDirtyFields = (ids: string[]) => {
                                const fields = new Set<string>();
                                ids.forEach(id => editMap[id]?.dirtyFields?.forEach(f => fields.add(f)));
                                return fields;
                            };

                            if (allSameDimensions) {
                                /* Sve gume identične — jedan editabilni red */
                                const firstEditState = editMap[allIds[0]] ?? initTireRowEditState(tires[0]);
                                const tireLabels = axleGroups.length <= 2
                                    ? axleGroups.flatMap(([pos, group]) => getTireLabels(pos, group.length, axleGroups.length))
                                    : allIds.map((_, i) => `${i + 1}`);
                                return (
                                    <TireAxleGroup
                                        label={`Sve gume`}
                                        tireIds={allIds}
                                        tireLabels={tireLabels}
                                        editState={firstEditState}
                                        isDirty={allIds.some(id => editMap[id]?.isDirty)}
                                        dirtyFields={getDirtyFields(allIds)}
                                        widthOptions={widthOptions}
                                        heightOptions={heightOptions}
                                        radiusOptions={radiusOptions}
                                        tireTypeOptions={tireTypeOptions}
                                        manufacturerOptions={manufacturerOptions}
                                        onChange={(field, value) => handleAxleChange(allIds, field, value)}
                                        onDelete={onDeleteClick}
                                    />
                                );
                            }

                            /* Različite dimenzije — editabilni red po osovini */
                            return axleGroups.map(([position, groupTires]) => {
                                const groupIds = groupTires.map(t => t.id);
                                const groupEditState = editMap[groupIds[0]] ?? initTireRowEditState(groupTires[0]);
                                const tireLabels = getTireLabels(position, groupIds.length, axleGroups.length);
                                return (
                                    <TireAxleGroup
                                        key={position}
                                        label={getAxleLabel(position, axleGroups.length)}
                                        tireIds={groupIds}
                                        tireLabels={tireLabels}
                                        editState={groupEditState}
                                        isDirty={groupIds.some(id => editMap[id]?.isDirty)}
                                        dirtyFields={getDirtyFields(groupIds)}
                                        widthOptions={widthOptions}
                                        heightOptions={heightOptions}
                                        radiusOptions={radiusOptions}
                                        tireTypeOptions={tireTypeOptions}
                                        manufacturerOptions={manufacturerOptions}
                                        onChange={(field, value) => handleAxleChange(groupIds, field, value)}
                                        onDelete={onDeleteClick}
                                    />
                                );
                            });
                        })()}
                    </tbody>
                </table>
            </div>
            <Loader isLoading={isSaving}> </Loader>
        </>
    );
});

TireInlineTable.displayName = "TireInlineTable";
export default memo(TireInlineTable);
